import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';
import { useCollection, useDoc } from '../data/firestore';
import { BugStatusChip } from './MyBugs';
import {
  AgentPanel, AttachmentGallery, MergeBuildStep, PrFacts, PromoteStep, stepClass, useActions, when,
} from '../components/shipRail';

// /admin/bugs/:id — one bug, start to finish:
//   1. Propose fix  → Cursor cloud agent on the control-plane repo, PR
//                     (POST /bugs/:id/propose, follow-ups via /message)
//   2. Merge & build → squash-merge the PR, Build → UAT (POST /bugs/:id/merge-build)
//   3. Approve       → promote the UAT-verified release to prod
//                     (POST /bugs/:id/approve, same gates as the Releases tab)
// The bug doc and the agent event stream are live (founder-readable);
// attachment links, the PR and the linked release come from GET /bugs/:id.
// Steps 2–3 are the shared ship rail (components/shipRail.js).

export default function AdminBugDetail() {
  const { id } = useParams();
  const { doc: live } = useDoc(id ? `bugReports/${id}` : null);
  const { docs: events } = useCollection(id ? `bugReports/${id}/events` : 'bugReports', { orderBy: 'seq', dir: 'asc' });
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [note, setNote] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [repoPick, setRepoPick] = useState('');

  const load = useCallback(async () => {
    if (!isBackendConfigured()) { setLoadError('The API is not configured.'); return; }
    try {
      setDetail(await apiGet(`/bugs/${id}`));
      setLoadError('');
    } catch (err) {
      setLoadError(err.message);
    }
  }, [id]);
  const { busy, error, notice, act } = useActions(load);

  useEffect(() => { load(); }, [load]);
  // Re-read presigned links / PR / release when the live doc moves.
  useEffect(() => { if (live) load(); }, [live?.status, live?.fix?.status, live?.fix?.prUrl, live?.fix?.releaseSha, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const bug = live || detail;
  const fix = bug?.fix || null;
  const agentBusy = fix?.status === 'running' || fix?.status === 'starting';
  const release = detail?.release || null;
  const pr = detail?.pr || null;
  const base = `/bugs/${id}`;

  const repo = fix?.repo || repoPick || detail?.route?.repo || 'plane';
  const propose = () => act('propose', async () => { await apiPost(`${base}/propose`, { prompt: note, repo }); setNote(''); return { message: `Agent started on the ${repo} repo — follow along below.` }; });
  const send = (text) => act('message', async () => { await apiPost(`${base}/message`, { prompt: text }); return { message: 'Follow-up sent.' }; });
  const cancel = () => act('cancel', () => apiPost(`${base}/cancel`, {}));
  const close = () => act('close', async () => { await apiPost(`${base}/close`, { reason: closeReason }); return { message: 'Closed.' }; });

  const closed = bug?.status === 'closed';
  const step = bug?.status === 'shipped' || closed ? 4 : fix?.releaseSha ? 3 : fix?.prUrl ? 2 : 1;
  const state = (n) => (step > n ? 'done' : step === n ? 'here' : '');

  if (!bug && loadError) return <div className="cs-store"><section className="cs-store-section"><p className="cs-land-micro">{loadError}</p></section></div>;
  if (!bug) return <div className="cs-store"><section className="cs-store-section"><p className="cs-help">Loading…</p></section></div>;

  return (
    <div className="cs-store">
      <header className="cs-store-hero">
        <p className="cs-help" style={{ margin: 0 }}><Link to="/admin/bugs">← Bug reports</Link></p>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>{bug.title || 'Bug report'} <BugStatusChip status={bug.status} /><span className={`mf-chip ${repo === 'kit' ? 'is-info' : ''}`} title={detail?.route?.reason || ''}>{repo} repo</span></h1>
        <p>
          {bug.name || bug.email} &lt;{bug.email}&gt;{bug.orgName ? ` · ${bug.orgName}` : ''} · {when(bug.createdAt)}
        </p>
      </header>
      <section className="cs-store-section">
        {(error || loadError) && <p className="cs-land-micro" role="alert">{error || loadError}</p>}
        {notice && <p className="cs-help" role="status">{notice}</p>}
        <div className="bug-detail">
          <div>
            <div className="cs-sw-card">
              <h3>Report</h3>
              <p className="bug-detail-desc">{bug.description || <span className="cs-help">(no description)</span>}</p>
              <dl className="mf-kv" style={{ marginTop: 12 }}>
                <dt>Page</dt><dd>{bug.context?.route || '—'}</dd>
                {bug.context?.appId ? <><dt>App</dt><dd>{bug.context.appId}</dd></> : null}
                <dt>Bundle</dt><dd>{bug.context?.buildMainJs || '—'}{bug.context?.planeEnv ? ` · ${bug.context.planeEnv}` : ''}</dd>
                <dt>Browser</dt><dd>{bug.context?.userAgent || '—'}{bug.context?.viewport ? ` · ${bug.context.viewport}` : ''}</dd>
                <dt>Mailed to</dt><dd>{bug.notifiedTo || (bug.meta?.status === 'error' ? `failed: ${bug.meta.message}` : 'pending')}</dd>
              </dl>
              <AttachmentGallery attachments={detail?.attachments || bug.attachments} />
            </div>
            <AgentPanel
              events={events}
              fix={fix}
              agentBusy={agentBusy}
              busy={busy}
              emptyText="No agent run yet. Propose a fix to start one."
              placeholder="Follow-up for the agent (it updates the same PR)"
              onSend={send}
              onCancel={cancel}
            />
          </div>

          <div className="bug-steps">
            <div className={stepClass(state(1))}>
              <div className="bug-step-head"><h4>1 · Propose a fix</h4>{fix?.prUrl ? <span className="mf-chip is-ok">PR open</span> : agentBusy ? <span className="mf-chip is-info">agent working</span> : null}</div>
              <p className="cs-help">Starts a Cursor cloud agent on <span className="aa-mono">{detail?.repo || '…'}</span> with the report, the described screenshots and the page context. It opens one PR.{detail?.route ? ` Triage says ${detail.route.repo} (${detail.route.reason}).` : ''}</p>
              {!fix?.agentId && (
                <>
                  <div className="bug-queue-filters" role="radiogroup" aria-label="Repository">
                    {['plane', 'kit'].map((r) => (
                      <button key={r} type="button" role="radio" aria-checked={repo === r} className={repo === r ? 'is-on' : ''} onClick={() => setRepoPick(r)} disabled={!!busy}>{r === 'kit' ? 'App Kit (instances)' : 'Control plane'}</button>
                    ))}
                  </div>
                  <textarea className="cs-input" rows={2} value={note} placeholder="Optional notes for the agent (where to look, what not to touch)" onChange={(e) => setNote(e.target.value)} disabled={!!busy} />
                </>
              )}
              <div className="bug-step-actions" data-agent-guard>
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={propose} disabled={!!busy || agentBusy || closed || (fix?.agentId && fix?.prUrl)}>
                  {busy === 'propose' ? 'Starting…' : fix?.agentId ? 'Start over' : 'Propose fix'}
                </button>
                {fix?.prUrl && <a className="cs-btn cs-btn-outline cs-btn-sm" href={fix.prUrl} target="_blank" rel="noreferrer">PR #{fix.prNumber || ''} ↗</a>}
              </div>
              <PrFacts pr={pr} />
            </div>

            <MergeBuildStep n={2} state={state(2)} base={base} fix={fix} release={release} status={bug.status} agentBusy={agentBusy} busy={busy} closed={closed} act={act} />
            <PromoteStep n={3} state={state(3)} base={base} fix={fix} release={release} status={bug.status} busy={busy} act={act} successMessage="Promote queued. The reporter is emailed when prod is live." />

            <div className={stepClass(closed ? 'done' : '')}>
              <div className="bug-step-head"><h4>Close</h4>{closed ? <span className="mf-chip">closed by {bug.closedBy || '—'}</span> : null}</div>
              {!closed && bug.status !== 'shipped' && (
                <div className="bug-step-actions" data-agent-guard>
                  <input className="cs-input" value={closeReason} placeholder="Reason (not a bug, duplicate, cannot reproduce…)" onChange={(e) => setCloseReason(e.target.value)} disabled={!!busy} style={{ flex: 1 }} />
                  <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={close} disabled={!!busy}>Close</button>
                </div>
              )}
              {bug.closedReason ? <p className="cs-help">{bug.closedReason}</p> : null}
              {bug.status === 'shipped' ? <p className="cs-help">Shipped {when(bug.shippedAt)} · reporter emailed.</p> : null}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
